import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth';
import { requestBillingAuth } from '@/features/store/common/api/payment.service';
import { toUserMessage } from '@platform/api-client';
import { setBillingIntent, subscriptionKeys } from '@platform/billing';
import { subscriptionApi } from '../api/subscription.api';

/**
 * 결제수단 변경 / 재활성화 진입 훅.
 *
 * 카드 재등록이 필요한 동작은 billing intent 를 심고 requestBillingAuth 로 이동하면,
 * 빌링 성공 페이지(PaymentBillingSuccessView)가 intent 로 분기해 실제 처리한다.
 * 재활성화는 "기존 카드로 즉시 재청구"(fast-path)를 먼저 시도할 수 있다.
 */
export function useBillingActions() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);

  const registerCard = useCallback(async () => {
    if (!user) return;
    await requestBillingAuth({
      customerKey: user.id,
      customerEmail: user.email,
      customerName: user.name,
    });
  }, [user]);

  /**
   * 결제수단(카드) 변경 — 카드 재등록 후 성공 페이지가 changePaymentMethod 호출.
   * returnTo: 완료 후 돌아갈 경로(기업 콘솔 등). 생략 시 마이페이지.
   */
  const changeCard = useCallback(
    async (subscriptionId: string, returnTo?: string) => {
      setBillingIntent({ purpose: 'change-card', subscriptionId, returnTo });
      await registerCard();
    },
    [registerCard],
  );

  /** 재활성화(새 카드) — 카드 재등록 후 성공 페이지가 reactivate 호출. */
  const reactivateWithNewCard = useCallback(
    async (subscriptionId: string) => {
      setBillingIntent({ purpose: 'reactivate', subscriptionId });
      await registerCard();
    },
    [registerCard],
  );

  /**
   * 재활성화(기존 카드) — 저장된 billingKey 로 즉시 재청구.
   * 실패하면 서버가 준 사유(카드 한도·약관 개정 등)를 돌려준다 → 호출부가 새 카드 등록으로 유도.
   */
  const reactivateExistingCard = useCallback(
    async (
      subscriptionId: string,
    ): Promise<{ ok: true } | { ok: false; message: string }> => {
      try {
        setBusy(true);
        await subscriptionApi.reactivate(subscriptionId);
        // react-query 밖에서 호출하므로 구독 캐시를 직접 비워야 마이페이지에 반영된다.
        void qc.invalidateQueries({ queryKey: subscriptionKeys.all });
        return { ok: true };
      } catch (error) {
        return {
          ok: false,
          message: toUserMessage(error, '등록된 카드로 결제에 실패했습니다.'),
        };
      } finally {
        setBusy(false);
      }
    },
    [qc],
  );

  return {
    busy,
    changeCard,
    reactivateWithNewCard,
    reactivateExistingCard,
  };
}
